import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Package } from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import { useProducts, useDeleteProduct } from '@/modules/products-api'
import { useCategories } from '@/modules/categories-api'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'

export function ProductsListPage() {
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [page, setPage] = useState(1)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data, isLoading } = useProducts({ page, limit: 20, q: q || undefined, categorySlug: cat || undefined })
  const { data: categories } = useCategories()
  const deleteMut = useDeleteProduct()
  const push = useToast((s) => s.push)

  const target = deleteId ? data.items.find((p) => p.id === deleteId) : null

  const handleDelete = async () => {
    if (!deleteId || !target) return
    try {
      await deleteMut.mutateAsync(deleteId)
      push('success', `Đã xóa "${target.name}"`)
    } catch (e: any) {
      push('error', e?.message ?? 'Lỗi mạng')
    }
    setDeleteId(null)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Sản phẩm</h1>
          <p className="text-sm text-muted mt-1">
            {isLoading ? 'Đang tải...' : `${data.total} sản phẩm trong hệ thống`}
          </p>
        </div>
        <Link
          to="/admin/san-pham/moi"
          className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Thêm sản phẩm
        </Link>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1) }}
            placeholder="Tìm theo tên, thương hiệu, slug..."
            className="w-full h-10 rounded-md border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:border-brand-500"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => { setCat(e.target.value); setPage(1) }}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand-500 min-w-[180px]"
        >
          <option value="">Tất cả danh mục</option>
          {categories.map((c) => <option key={c.id} value={c.slug}>{c.label}</option>)}
        </select>
        <div className="text-sm text-muted ml-auto">Trang {data.page}/{data.totalPages}</div>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Sản phẩm</th>
                <th className="text-left px-4 py-3 font-medium">Danh mục</th>
                <th className="text-left px-4 py-3 font-medium">Thương hiệu</th>
                <th className="text-right px-4 py-3 font-medium">Giá</th>
                <th className="text-center px-4 py-3 font-medium">Giảm</th>
                <th className="text-center px-4 py-3 font-medium">Kho</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                      <Package className="w-8 h-8" />
                    </div>
                    <div className="font-semibold text-text">Không tìm thấy sản phẩm</div>
                  </td>
                </tr>
              )}
              {data.items.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-md bg-slate-100 overflow-hidden border border-border shrink-0">
                        <img
                          src={p.image} alt={p.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23e2e8f0"/></svg>'
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-text line-clamp-1">{p.name}</div>
                        <div className="text-xs text-muted font-mono">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted">{p.categoryLabel}</td>
                  <td className="px-4 py-3 text-muted">{p.brand}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold text-text">{formatVND(p.price)}</div>
                    {p.oldPrice > p.price && (
                      <div className="text-xs text-muted line-through">{formatVND(p.oldPrice)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {p.discountPct > 0
                      ? <span className="bg-red-50 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">-{p.discountPct}%</span>
                      : <span className="text-muted text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {p.inStock
                      ? <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Còn hàng</span>
                      : <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">Hết hàng</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/admin/san-pham/${p.id}`}
                        className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center transition"
                        aria-label="Sửa"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteId(p.id)}
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center transition"
                        aria-label="Xóa"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.totalPages > 1 && (
          <div className="border-t border-border p-4 flex items-center justify-center gap-2">
            {Array.from({ length: data.totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={[
                  'min-w-[36px] h-9 rounded-md border px-3 text-sm font-medium transition',
                  n === data.page ? 'bg-brand-600 border-brand-600 text-white' : 'bg-surface border-border text-text hover:border-brand-500',
                ].join(' ')}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Xóa sản phẩm"
        message={`Bạn chắc chắn muốn xóa "${target?.name}"? Hành động không thể hoàn tác.`}
        confirmLabel="Xóa"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
